import React, { useState, useEffect } from 'react';

import { SENDING_TYPE_OPTIONS } from '@vlabs/api-bindings/src/luna-client/constants';
import { InfoCircleIcon } from '@vlabs/icons';
import validate from '@vlabs/shared/validators';
import {
  Control, SettingsItemWrapper, ButtonSwitch, ButtonSwitchGroup, Tooltip,
} from '@vlabs/uikit';
import { isNil } from 'lodash';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { defaultValues } from '@vlabs/pages/video-analytics/defaultFormValues';

import { DroiForm } from './DroiForm';
import { EventPolicySubForm } from './EventPolicySubForm';
import { SaveToArchiveSubForm } from './SaveToArchiveSubForm';
import st from './SharedSubFormStyles.module.sass';
import { TargetsOverviewSubForm } from './TargetsOverviewSubForm';
import {
  CrowdThresholdSettingsModal,
  CrowdThresholdSettingsTooltipPreview,
} from '../../modal/CrowdThresholdSettingsModal';
import { FrameAnalysisFrequencyModal } from '../../modal/FrameAnalysisFrequencyModal';

const PEOPLE_COUNT_MODE = {
  GTE: 'gte',
  LTE: 'lte',
};

const commonStylesProps = {
  className: st.SettingItem,
  titleClassName: st.SettingItem__Title,
  contentClassName: st.SettingItem__Content,
};

export function CrowdSubForm({ fieldName }) {
  const { t } = useTranslation();
  const {
    register, control, setValue, formState: { errors },
  } = useFormContext();
  const [activeTab, setActiveTab] = useState(PEOPLE_COUNT_MODE.GTE);

  const createFullName = (name) => (fieldName ? `${fieldName}.${name}` : name);

  const frameRateAnalysisType = useWatch({ name: createFullName('parameters.parameters.rate.unit') })?.value;
  const peopleCountGte = useWatch({ name: createFullName('parameters.parameters.people_count__gte') });
  const peopleCountLte = useWatch({ name: createFullName('parameters.parameters.people_count__lte') });
  const targetsField = useWatch({ name: createFullName('parameters.targets'), defaultValue: [] });
  const isTargetsCoordinatesActive = targetsField.includes('coordinates');

  useEffect(() => {
    if (!isNil(peopleCountGte)) {
      setActiveTab(PEOPLE_COUNT_MODE.GTE);
    }
    if (!isNil(peopleCountLte)) {
      setActiveTab(PEOPLE_COUNT_MODE.LTE);
    }
  }, []);

  const onTabChange = (value) => {
    setActiveTab(value);
    // при смене вкладок мы очищаем противоположное значение,
    // старое значение перемещается в противоположное поле
    // поэтому если value === gte, то идет очистка lte и наоборот
    if (value === PEOPLE_COUNT_MODE.LTE) {
      setValue(createFullName('parameters.parameters.people_count__lte'), peopleCountGte);
      setValue(createFullName('parameters.parameters.people_count__gte'), '');
    } else if (value === PEOPLE_COUNT_MODE.GTE) {
      setValue(createFullName('parameters.parameters.people_count__gte'), peopleCountLte);
      setValue(createFullName('parameters.parameters.people_count__lte'), '');
    }
  };

  return (
    <React.Fragment>
      <div className={st.SettingsGroup}>
        <div className={st.SettingsGroup__Header}>
          <span>{t('videoAnalytics:form.crowd.threshold_settings.title')}</span>
          <Tooltip
            className={st.Tooltip}
            modalContent={<CrowdThresholdSettingsModal />}
            tooltipContent={<CrowdThresholdSettingsTooltipPreview />}
            tooltipProps={{ effect: 'solid' }}
          >
            <InfoCircleIcon className={st.Tooltip_icon} />
          </Tooltip>
        </div>

        <SettingsItemWrapper
          title={t('videoAnalytics:form.crowd.people count mode.label')}
          {...commonStylesProps}
        >
          <ButtonSwitchGroup
            className={st.Tabs}
            onChange={onTabChange}
            testId="tabsSwitcher.peopleCountMode"
            value={activeTab}
            variant="square"
          >
            <ButtonSwitch value={PEOPLE_COUNT_MODE.GTE}>
              {t('videoAnalytics:form.crowd.people count mode.gte')}
            </ButtonSwitch>

            <ButtonSwitch value={PEOPLE_COUNT_MODE.LTE}>
              {t('videoAnalytics:form.crowd.people count mode.lte')}
            </ButtonSwitch>
          </ButtonSwitchGroup>
        </SettingsItemWrapper>

        <SettingsItemWrapper
          data-tooltip-content={t('videoAnalytics:form.crowd.people_count.tooltip')}
          title={t('videoAnalytics:form.crowd.people_count.label')}
          {...commonStylesProps}
        >
          {activeTab === PEOPLE_COUNT_MODE.GTE && (
            <Control.Input
              {...register(createFullName('parameters.parameters.people_count__gte'), {
                min: validate.gte(0),
                validate: validate.integer(),
              })}
              errors={errors}
            />
          )}
          {activeTab === PEOPLE_COUNT_MODE.LTE && (
            <Control.Input
              {...register(createFullName('parameters.parameters.people_count__lte'), {
                min: validate.gte(0),
                validate: validate.integer(),
              })}
              errors={errors}
            />
          )}
        </SettingsItemWrapper>

        <SettingsItemWrapper
          title={t('videoAnalytics:form.crowd.probe_count')}
          {...commonStylesProps}
        >
          <Control.Input
            {...register(createFullName('parameters.parameters.probe_count'), {
              min: validate.gte(0),
              validate: validate.integer(),
            })}
            errors={errors}
          />
        </SettingsItemWrapper>
      </div>

      <div className={st.SettingsGroup}>
        <SettingsItemWrapper
          data-tooltip-content={t('videoAnalytics:form.crowd.event_delay.tooltip')}
          title={t('videoAnalytics:form.crowd.event_delay.label')}
          {...commonStylesProps}
        >
          <Control.Input
            {...register(createFullName('parameters.parameters.event_delay'), {
              min: validate.gte(0),
              validate: validate.integer(),
            })}
            errors={errors}
          />
        </SettingsItemWrapper>

        <SettingsItemWrapper
          title={t('videoAnalytics:form.rate.unit.label')}
          {...commonStylesProps}
        >
          <Control.Select
            control={control}
            isClearable
            name={createFullName('parameters.parameters.rate.unit')}
            options={SENDING_TYPE_OPTIONS.raw}
          />
        </SettingsItemWrapper>

        <SettingsItemWrapper
          data-tooltip-content={t('videoAnalytics:form.rate.period.tooltip')}
          modalContent={<FrameAnalysisFrequencyModal />}
          title={t('videoAnalytics:form.rate.period.label')}
          {...commonStylesProps}
        >
          <Control.Input
            {...register(createFullName('parameters.parameters.rate.period'), {
              min: frameRateAnalysisType === 'frame'
                ? validate.gte(1)
                : validate.gte(0.001),
              validate: frameRateAnalysisType === 'frame'
                ? validate.integer()
                : validate.number(),
            })}
            errors={errors}
          />
        </SettingsItemWrapper>

        <EventPolicySubForm fieldName={fieldName} />

        <SettingsItemWrapper
          title={t('videoAnalytics:form.ROI.label')}
          {...commonStylesProps}
        >
          <Controller
            control={control}
            name={createFullName('parameters.parameters.roi')}
            render={({ field: { value, onChange } }) => {
              return (
                <Control.CoordinatesInput
                  errors={errors}
                  name={createFullName('parameters.parameters.roi')}
                  onChange={onChange}
                  value={value ?? defaultValues.roi}
                  variant="modern"
                />
              );
            }}
            rules={{ validate: validate.region }}
          />
        </SettingsItemWrapper>

        <DroiForm name={createFullName('parameters.parameters.droi')} />

        <SettingsItemWrapper
          title={t('videoAnalytics:form.crowd.targets.coordinates')}
          {...commonStylesProps}
        >
          <Control.Checkbox
            id={createFullName('parameters.targets.coordinates')}
            label={t(`videoAnalytics:form.${isTargetsCoordinatesActive ? 'enabled' : 'disabled'}`)}
            value="coordinates"
            {...register(createFullName('parameters.targets'))}
          />
        </SettingsItemWrapper>

        <TargetsOverviewSubForm
          analytic="crowd"
          fieldName={fieldName}
        />

        <SaveToArchiveSubForm fieldName={fieldName} />
      </div>
    </React.Fragment>
  );
}
